import { useRender } from '@base-ui/react/use-render'
import { type MouseEvent } from 'react'

import { useTransientFlag } from '../../../hooks/use-transient-flag.ts'
import { cn } from '../../../lib/utils.ts'
import { Spinner } from '../spinner/spinner.tsx'

/**
 * Attributes carried only while the guard is inert. Set after `...rest` so they win over the host,
 * and spread conditionally rather than as `undefined` so the idle path never clears an attribute the
 * host set for itself.
 */
const coolingDownProps = {
  'aria-disabled': true,
  'aria-busy': true,
  // `data-disabled` is the attribute the styled hosts already key their disabled treatment on, so a
  // guard plugged into a `Button` dims itself. `data-cooling-down` tells this cooldown apart from a
  // genuinely disabled control, for a caller who wants to style it differently.
  'data-disabled': '',
  'data-cooling-down': '',
}

/**
 * ClickGuard is a link that can only be followed once per cooldown, and it owns the whole visual
 * answer to that cooldown. **It is the anchor**, not a decoration on someone else's element — which
 * is why it carries its own `href`. It brings no styling, so it takes the look of wherever it sits:
 * plug it into a styled primitive's `render`, or hand it that primitive's classes directly.
 *
 * @example
 * <Item.Root interactive render={<ClickGuard href={mailtoHref(email)} />}>…</Item.Root>
 * <ClickGuard href={telHref(phoneNumber)} className={buttonVariants({ variant: 'secondary' })}>…</ClickGuard>
 *
 * It knows nothing about URL schemes — build a `tel:` / `mailto:` URL with `telHref` / `mailtoHref`
 * from `@orus.eu/pharaoh-next/lib/contact-href`.
 *
 * For 1.5 s after a click the guard goes inert. Two halves, because neither covers the other:
 * `pointer-events: none` stops the pointer reaching the element at all (second click, middle-click,
 * context menu — and the cursor falls back to the ancestor's, so the hand goes away), and the click
 * handler bails out on the keyboard path, where CSS has no say. The `href` is never rewritten: the
 * destination stays, the ways of reaching it are what close. The content fades out under a `Spinner`
 * that takes the place it occupied; it stays in flow, so the box keeps its size — the same
 * no-layout-shift trick as `LoadingButton`. This holds for an inline host too (an address stitched
 * into a sentence via `translateWithComponentsIn`), not just the styled `Button` / `Item` hosts.
 *
 * **Links only, by design.** It renders an `<a>` and nothing else, because an element that owns an
 * `href` is an anchor. Guarding a `<button>` action is deliberately out of scope: a `render` target
 * replaces the host's element, so it could not leave a real button intact — that job belongs to a
 * hook, to be written when a call site actually needs one.
 *
 * **States** — style against these attributes:
 * - `[data-cooling-down]` — the cooldown, for a caller who wants a treatment of their own.
 * - `[data-disabled]` — set during the cooldown too, so a host that already styles `data-disabled:`
 *   dims itself with nothing to wire.
 * - `[aria-busy]` / `[aria-disabled]` — the cooldown, announced to assistive tech.
 *
 * @summary Link that can only be followed once per cooldown, with its own busy feedback.
 */
function ClickGuard(props: ClickGuard.Props) {
  const { render, ref, className, children, onClick, ...rest } = props
  const { active: isCoolingDown, trigger } = useTransientFlag()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (isCoolingDown) {
      // The keyboard half of the guard: Enter on a focused link fires a click, and no amount of
      // `pointer-events` reaches it. Without this the cooldown would only hold for the mouse.
      event.preventDefault()
      return
    }
    trigger()
    onClick?.(event)
  }

  return useRender({
    render,
    ref,
    defaultTagName: 'a',
    props: {
      'data-slot': 'click-guard',
      ...rest,
      className: cn(
        `
          group/click-guard relative appearance-none bg-transparent text-left no-underline
          data-cooling-down:pointer-events-none
        `,
        className,
      ),
      onClick: handleClick,
      ...(isCoolingDown ? coolingDownProps : undefined),
      children: (
        <>
          {/*
            `display: contents` while idle: the wrapper exists only to have a box to fade, and a box
            would change how the host lays its children out (an `Item.Content` with `flex-1` stops
            filling the row, an `ml-auto` stops reaching its edge). Contents makes it invisible to
            layout, so the idle state is byte-for-byte the host's own. It becomes a real flex box
            only during the cooldown, and inherits the host's `gap` so the box keeps the width it had.
            `inline-flex`, not `flex`: the anchor itself can be `inline` (guarding an address stitched
            into a sentence via `translateWithComponentsIn`), and an inline box around a block-level
            child breaks the surrounding text's formatting context, forcing a line break where none
            existed. Inside a flex host (`Button`, `Item`) this is a no-op — CSS blockifies a flex
            item's `display` regardless of `inline-flex` vs `flex` — so the styled hosts see no change.
          */}
          <span
            data-slot="click-guard-content"
            className={`
              contents
              group-data-cooling-down/click-guard:inline-flex group-data-cooling-down/click-guard:items-center
              group-data-cooling-down/click-guard:gap-[inherit] group-data-cooling-down/click-guard:opacity-0
            `}
          >
            {children}
          </span>
          {/*
            The spinner takes the place the content had rather than a fixed corner: the overlay
            spans the element and inherits its padding and `justify-content`, so the spinner lands
            where the host puts its content — centred in a `Button`, on the leading edge of an
            `Item` row, right where the eye and the pointer already were.

            The Spinner keeps its own role="status"/aria-label="Loading" — do NOT add aria-hidden,
            or screen readers stop announcing the busy state. The faded content keeps its opacity,
            so the accessible name survives and composes into it (→ "<label> Loading").
          */}
          {isCoolingDown ? (
            <span
              data-slot="click-guard-spinner"
              className="absolute inset-0 flex items-center [justify-content:inherit] p-[inherit]"
            >
              <Spinner className="size-5" />
            </span>
          ) : null}
        </>
      ),
    },
  })
}

namespace ClickGuard {
  /** An anchor's props, `href` included. A styled host merges its own on top through `render`. */
  export type Props = useRender.ComponentProps<'a'>
}

export { ClickGuard }
